import {
    deleteApiProjectsPropertyByPropertyId,
    getApiProjects,
    postApiProjectsProjectNameExists,
    postApiProjectsPropertyExists,
    getApiProjectsByProjectIdFiles,
    deleteApiProjectsByProjectIdFiles,
    getApiProjectsByProjectIdByAnnualizationTypeOperatingStatementPeriods,
    getApiProjectsByProjectIdOperatingStatementExport,
    getApiProjectsByProjectIdExtractionByExtractionIdOverlappingPeriods,
    getApiProjectsByProjectIdExtractionByExtractionIdConflictedExtractions,
    postApiProjectsByProjectIdExtractionByExtractionIdConvertToDraft,
    getApiProjectsByProjectIdOperatingStatement,
    postApiProjectsPropertyNameExists,
    postApiProjectsByProjectIdFilesUpload,
    getApiProjectsPropertyInfoForDeleteByPropertyId,
    getApiProjectsByProjectId,
    putApiProjectsPropertyUpdate,
    deleteApiProjectsByProjectId,
    postApiProjects,
    getApiProjectsByProjectIdProperty,
    postApiProjectsPropertyCreate,
    putApiProjectsUpdate,
    getApiProjectsByDealByDealId,
    getApiProjectsByProjectIdRentRollLocation,
} from "@/generated";
import type {
    NewProject,
    NewProperty,
    BasePropertyInfoWithId,
    ProjectNameCheckInfo,
    PropertyNameCheckInfo,
    PropertyAddressCheckInfo,
    AnnualizationType,
    ProjectInfo,
    MultipropDeal,
} from "@/generated";
import type { TExportConfig } from "./types";
import { exportConfigToQuery } from "./types";
import { handleApiResponse } from "./handleApiResponse";
import { handleApiResponseAssertData } from "./handleApiResponseAssertData";

// Standalone API for legacy redirect usage (kept in APIs layer to satisfy lint rules)
export const getProjectByDealId = async (
    dealId: number,
): Promise<MultipropDeal> => {
    const { data } = await handleApiResponseAssertData(() =>
        getApiProjectsByDealByDealId({ path: { deal_id: dealId } }),
    );
    return data;
};

const getHeaders = (databaseIds: number[]) => ({
    "x-company-databases": databaseIds.join(","),
});

export const createApiDeals = ({
    getAccessToken,
    baseWsUrl,
}: {
    getAccessToken: () => Promise<string>;
    baseWsUrl: string;
}) => ({
    multipropCreateDeal: async (newDeal: NewProject) => {
        return handleApiResponseAssertData(() =>
            postApiProjects({ body: newDeal }),
        );
    },
    multipropUpdateDeal: async (deal: ProjectInfo) =>
        handleApiResponse(() => putApiProjectsUpdate({ body: deal })),
    multipropCreateProperty: async (newProperty: NewProperty) => {
        return handleApiResponseAssertData(() =>
            postApiProjectsPropertyCreate({ body: newProperty }),
        );
    },
    multipropUpdateProperty: async (
        property: BasePropertyInfoWithId,
        projectId: number,
        databaseId: number,
    ) =>
        handleApiResponse(() =>
            putApiProjectsPropertyUpdate({
                body: property,
                query: {
                    project_id: projectId,
                    database_id: databaseId,
                },
            }),
        ),
    multipropGetDeal: async ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) => {
        return handleApiResponseAssertData(() =>
            getApiProjectsByProjectId({
                path: { project_id: projectId },
                query: {
                    database_id: databaseId,
                },
            }),
        );
    },
    multipropDeleteProperty: async ({
        propertyId,
        databaseId,
        projectId,
    }: {
        propertyId: number;
        databaseId: number;
        projectId: number;
    }) =>
        handleApiResponse(() =>
            deleteApiProjectsPropertyByPropertyId({
                path: { property_id: propertyId },
                query: {
                    database_id: databaseId,
                    project_id: projectId,
                },
            }),
        ),
    getPropertyInfoForDelete: async ({
        propertyId,
        databaseId,
        projectId,
    }: {
        propertyId: number;
        databaseId: number;
        projectId: number;
    }) =>
        handleApiResponseAssertData(() =>
            getApiProjectsPropertyInfoForDeleteByPropertyId({
                path: { property_id: propertyId },
                query: {
                    database_id: databaseId,
                    project_id: projectId,
                },
            }),
        ),
    // Overview rent-roll rows carry no file identity; resolve it on click.
    getRentRollLocation: async ({
        projectId,
        databaseId,
        extractionId,
    }: {
        projectId: number;
        databaseId: number;
        extractionId: number;
    }) =>
        handleApiResponseAssertData(() =>
            getApiProjectsByProjectIdRentRollLocation({
                path: { project_id: projectId },
                query: {
                    database_id: databaseId,
                    extraction_id: extractionId,
                },
            }),
        ),
    getAllDeals: async (databaseIds: number[]) => {
        const headers = getHeaders(databaseIds);
        return handleApiResponseAssertData(() => getApiProjects({ headers }));
    },
    deleteDeal: async ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) =>
        handleApiResponse(() =>
            deleteApiProjectsByProjectId({
                path: { project_id: projectId },
                query: { database_id: databaseId },
            }),
        ),
    getDeal: async ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) =>
        handleApiResponseAssertData(() =>
            getApiProjectsByProjectIdProperty({
                path: { project_id: projectId },
                query: { database_id: databaseId },
            }),
        ),
    projectNameCheck: async (projectNameCheckInfo: ProjectNameCheckInfo) =>
        handleApiResponseAssertData(() =>
            postApiProjectsProjectNameExists({ body: projectNameCheckInfo }),
        ),
    propertyAddressCheck: async (
        propertyAddressCheckInfo: PropertyAddressCheckInfo,
    ) =>
        handleApiResponseAssertData(() =>
            postApiProjectsPropertyExists({ body: propertyAddressCheckInfo }),
        ),
    propertyNameCheck: async (propertyNameCheckInfo: PropertyNameCheckInfo) =>
        handleApiResponseAssertData(() =>
            postApiProjectsPropertyNameExists({ body: propertyNameCheckInfo }),
        ),
    addDealFile: async ({
        projectId,
        databaseId,
        file,
    }: {
        projectId: number;
        databaseId: number;
        file: Blob | File;
    }) =>
        handleApiResponseAssertData(() =>
            postApiProjectsByProjectIdFilesUpload({
                path: { project_id: projectId },
                body: { upload_file: file },
                query: { database_id: databaseId },
            }),
        ),
    getDealFiles: async ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) =>
        handleApiResponseAssertData(() =>
            getApiProjectsByProjectIdFiles({
                path: { project_id: projectId },
                query: { database_id: databaseId },
            }),
        ),
    deleteFiles: async ({
        projectId,
        databaseId,
        fileIds,
    }: {
        projectId: number;
        databaseId: number;
        fileIds: number[];
    }) =>
        handleApiResponse(() =>
            deleteApiProjectsByProjectIdFiles({
                path: { project_id: projectId },
                query: { database_id: databaseId, ids: fileIds },
            }),
        ),
    getOperatingStatementPeriods: async ({
        projectId,
        databaseId,
        annualizationType,
    }: {
        projectId: number;
        databaseId: number;
        annualizationType: AnnualizationType;
    }) =>
        handleApiResponseAssertData(() =>
            getApiProjectsByProjectIdByAnnualizationTypeOperatingStatementPeriods(
                {
                    path: {
                        project_id: projectId,
                        annualization_type: annualizationType,
                    },
                    query: {
                        database_id: databaseId,
                    },
                },
            ),
        ),
    getOperatingStatementByDealId: async ({
        projectId,
        databaseId,
        startDate,
        endDate,
        interval,
        annualizationType,
        reconciliation,
    }: {
        projectId: number;
        databaseId: number;
        startDate: string;
        endDate: string;
        interval?: number;
        annualizationType: AnnualizationType;
        reconciliation?: boolean;
    }) =>
        getApiProjectsByProjectIdOperatingStatement({
            path: { project_id: projectId },
            query: {
                from: startDate,
                to: endDate,
                interval,
                database_id: databaseId,
                annualization_type: annualizationType,
                reconciliation,
            },
        }),
    getOverlappingPeriodsByDealIdAndExtractionId: async ({
        projectId,
        databaseId,
        extractionId,
    }: {
        projectId: number;
        databaseId: number;
        extractionId: number;
    }) =>
        handleApiResponseAssertData(() =>
            getApiProjectsByProjectIdExtractionByExtractionIdOverlappingPeriods(
                {
                    path: {
                        project_id: projectId,
                        extraction_id: extractionId,
                    },
                    query: { database_id: databaseId },
                },
            ),
        ),
    getConflictExtractionsByDealIdAndExtractionId: async ({
        projectId,
        databaseId,
        extractionId,
    }: {
        projectId: number;
        databaseId: number;
        extractionId: number;
    }) =>
        handleApiResponseAssertData(() =>
            getApiProjectsByProjectIdExtractionByExtractionIdConflictedExtractions(
                {
                    path: {
                        project_id: projectId,
                        extraction_id: extractionId,
                    },
                    query: { database_id: databaseId },
                },
            ),
        ),
    convertExtractionsToDraft: async ({
        projectId,
        databaseId,
        extractionId,
        extractionIds,
    }: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionIds: number[];
    }) =>
        handleApiResponse(() =>
            postApiProjectsByProjectIdExtractionByExtractionIdConvertToDraft({
                path: {
                    project_id: projectId,
                    extraction_id: extractionId,
                },
                query: {
                    database_id: databaseId,
                    extraction_ids: extractionIds,
                },
            }),
        ),
    downloadAnnualizationFile: async ({
        projectId,
        databaseId,
        startDate,
        endDate,
        interval,
        exportConfig,
        nonOperatingAccountsInCoa,
        annualizationType,
        reconciliation,
    }: {
        projectId: number;
        databaseId: number;
        startDate: string;
        endDate: string;
        interval: number;
        exportConfig: TExportConfig;
        nonOperatingAccountsInCoa: boolean;
        annualizationType: AnnualizationType;
        reconciliation?: boolean;
    }) =>
        handleApiResponse(() =>
            getApiProjectsByProjectIdOperatingStatementExport({
                path: { project_id: projectId },
                query: {
                    database_id: databaseId,
                    from: startDate,
                    to: endDate,
                    interval: interval,
                    annualization_type: annualizationType,
                    reconciliation,
                    ...exportConfigToQuery(
                        exportConfig,
                        nonOperatingAccountsInCoa,
                    ),
                },
            }),
        ),
    subscribeToDealUpdates: ({
        projectId,
        databaseId,
        onConnectionOpened,
        onConnectionClosed,
        onFileUpdate,
        onPropertyUpdate,
        onError,
    }: {
        projectId: number;
        databaseId: number;
        onConnectionOpened?: () => void;
        onConnectionClosed?: () => void;
        onFileUpdate?: (data: {
            projectId: number;
            databaseId: number;
        }) => Promise<void>;
        onPropertyUpdate?: (data: {
            projectId: number;
            databaseId: number;
        }) => Promise<void>;
        onError?: (error: Error) => void;
    }) => {
        let websocket: WebSocket | null = null;
        let reconnectTimeout: NodeJS.Timeout | null = null;
        let heartbeatInterval: NodeJS.Timeout | null = null;
        let reconnectAttempts = 0;
        let isDestroyed = false;

        const cleanup = () => {
            isDestroyed = true;
            if (websocket) {
                websocket.close();
                websocket = null;
            }
            if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
                reconnectTimeout = null;
            }
            if (heartbeatInterval) {
                clearInterval(heartbeatInterval);
                heartbeatInterval = null;
            }
        };

        const cleanupCurrentConnection = () => {
            if (websocket) {
                websocket.close();
                websocket = null;
            }
            if (heartbeatInterval) {
                clearInterval(heartbeatInterval);
                heartbeatInterval = null;
            }
        };

        const connect = async () => {
            if (isDestroyed) return;

            let accessToken: string;
            try {
                accessToken = await getAccessToken();
                if (isDestroyed) return;

                websocket = new WebSocket(
                    `${baseWsUrl}/ws/databases/${databaseId}/project-updates/${projectId}`,
                );
            } catch (error) {
                console.error("Error creating WebSocket:", error);
                if (!isDestroyed) {
                    onError?.(
                        new Error(
                            `Failed to create WebSocket connection for project ${projectId}`,
                        ),
                    );
                }
                return;
            }

            websocket.onopen = () => {
                if (isDestroyed) {
                    websocket?.close();
                    return;
                }

                websocket?.send(accessToken);

                reconnectAttempts = 0;
                onConnectionOpened?.();

                heartbeatInterval = setInterval(() => {
                    if (
                        websocket?.readyState === WebSocket.OPEN &&
                        !isDestroyed
                    ) {
                        websocket.send("ping");
                    }
                }, 15000);
            };

            websocket.onmessage = event => {
                if (isDestroyed) return;
                try {
                    const message = JSON.parse(event.data as string) as {
                        type: string;
                        deal_id?: number;
                        data?: unknown;
                    };

                    switch (message.type) {
                        case "connected":
                            console.debug(
                                "WebSocket connected for project",
                                projectId,
                            );
                            break;
                        case "rentroll_synced":
                        case "extraction_completed":
                        case "extraction_incomplete":
                        case "extraction_deleted":
                        case "file_deleted":
                            void onFileUpdate?.({ projectId, databaseId });
                            break;
                        case "property_updated":
                        case "property_deleted":
                            void onPropertyUpdate?.({ projectId, databaseId });
                            break;
                        case "ping":
                            websocket?.send("pong");
                            break;
                        case "pong":
                            break;
                        default:
                            console.debug(
                                "Unknown message type:",
                                message.type,
                            );
                    }
                } catch (error) {
                    console.warn("Failed to parse WebSocket message:", error);
                }
            };

            websocket.onclose = () => {
                if (isDestroyed) return;

                cleanupCurrentConnection();
                onConnectionClosed?.();

                const delay = Math.min(
                    1000 * Math.pow(2, reconnectAttempts),
                    30000,
                );
                reconnectAttempts++;

                reconnectTimeout = setTimeout(() => {
                    if (!isDestroyed) {
                        console.debug(
                            `Reconnecting WebSocket (attempt ${reconnectAttempts})...`,
                        );
                        void connect();
                    }
                }, delay);
            };

            websocket.onerror = error => {
                if (!isDestroyed) {
                    console.error("WebSocket error:", error);
                    onError?.(
                        new Error(
                            `WebSocket connection error for project ${projectId}`,
                        ),
                    );
                }
            };
        };

        void connect();
        return cleanup;
    },
});
